<template>
  <div class="comic-detail-page">
    <!-- 左侧导航栏 -->
    <AppSidebar />

    <div class="comic-detail-container">
      <div class="comic-detail-content">
        <div v-if="loading" class="loading">
          <div class="loading-spinner"></div>
          <div class="loading-text">加载中...</div>
        </div>

        <div v-else-if="error" class="error-container">
          <div class="error-icon">!</div>
          <div class="error-message">{{ error }}</div>
          <button class="retry-button" @click="fetchComicDetail">重试</button>
        </div>

        <div v-else-if="comicDetail" class="comic-detail">
          <!-- 漫画头部信息 -->
          <div class="comic-header">
            <div class="comic-cover">
              <img :src="BASE_URL + comicDetail.comic.cover" :alt="comicDetail.comic.title" />
            </div>
            <div class="comic-info">
              <h1 class="comic-title">{{ comicDetail.comic.title }}</h1>
              <div class="comic-meta">
                <span class="author">作者：{{ comicDetail.comic.author }}</span>
                <span class="category">分类：{{ comicDetail.comic.category_name }}</span>
                <span class="status">状态：{{ comicDetail.comic.status_text }}</span>
                <span class="update-time">更新时间：{{ formatTimestamp(comicDetail.comic.updatetime) }}</span>
                <span class="views">浏览量：{{ comicDetail.comic.views }}</span>
                <span class="likes">点赞数：{{ comicDetail.comic.likes }}</span>
              </div>
              <div class="comic-tags" v-if="comicDetail.comic.tags">
                <span class="tag" v-for="(tag, index) in comicDetail.comic.tags.split(',')" :key="index">{{ tag }}</span>
              </div>
              <div class="comic-price" v-if="comicDetail.comic.is_free === 0">
                <span class="price">{{ comicDetail.comic.price }} 元</span>
                <span class="free-chapters">{{ comicDetail.comic.free_chapters_text }}</span>
              </div>
              <div class="comic-description">
                <p>{{ comicDetail.comic.description }}</p>
              </div>
            </div>
          </div>

          <!-- 章节列表 -->
          <div class="comic-chapters">
            <h2 class="chapters-title">章节列表</h2>
            <div class="chapters-grid">
              <div 
                v-for="chapter in comicDetail.chapters" 
                :key="chapter.id" 
                class="chapter-item"
                :class="{ 'free': chapter.is_free === 1, 'paid': chapter.is_free === 0 }"
              >
                <div class="chapter-info">
                  <span class="chapter-title">{{ chapter.title }}</span>
                  <span class="chapter-number">第{{ chapter.chapter_number }}话</span>
                  <div class="chapter-status">
                    <span v-if="chapter.is_free === 1" class="free-tag">免费</span>
                    <span v-else class="price-tag">{{ chapter.price }}元</span>
                  </div>
                </div>
                <div class="chapter-actions">
                  <button class="read-button" @click="navigateToReader(chapter.id)">阅读</button>
                </div>
              </div>
            </div>
          </div>

          <!-- 相关推荐 -->
          <div class="related-comics">
            <h2 class="related-title">相关推荐</h2>
            <div v-if="relatedComicsLoading" class="loading-small">
              <div class="loading-spinner-small"></div>
              <div class="loading-text-small">加载中...</div>
            </div>
            <div v-else-if="relatedComicsError" class="error-message-small">
              {{ relatedComicsError }}
            </div>
            <div v-else class="related-grid">
              <router-link 
                v-for="comic in relatedComics" 
                :key="comic.id" 
                :to="`/comic/${comic.id}`" 
                class="related-comic-item"
              >
                <div class="related-comic-cover">
                  <img :src="BASE_URL + comic.cover" :alt="comic.title" />
                </div>
                <div class="related-comic-info">
                  <div class="related-comic-title">{{ comic.title }}</div>
                  <div class="related-comic-author">{{ comic.author }}</div>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <AppFooter />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getComicDetail, getRelatedComics } from '@/api/comic';
import { BASE_URL } from '@/api/config';
import AppSidebar from '@/components/layout/Sidebar.vue';
import AppFooter from '@/components/layout/Footer.vue';

export default {
  name: 'ComicDetail',
  components: {
    AppSidebar,
    AppFooter
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const comicDetail = ref(null);
    const loading = ref(true);
    const error = ref(null);
    
    const relatedComics = ref([]);
    const relatedComicsLoading = ref(false);
    const relatedComicsError = ref(null);

    const fetchComicDetail = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const id = route.params.id;
        if (!id) {
          throw new Error('漫画ID不能为空');
        }
        
        const data = await getComicDetail(id);
        if (!data) {
          throw new Error('获取漫画详情失败');
        }
        
        comicDetail.value = data;
        // 获取详情成功后，加载相关推荐
        fetchRelatedComics(id);
      } catch (err) {
        console.error('获取漫画详情出错:', err);
        error.value = err.message || '获取漫画详情失败，请稍后再试';
      } finally {
        loading.value = false;
      }
    };
    
    const fetchRelatedComics = async (comicId) => {
      relatedComicsLoading.value = true;
      relatedComicsError.value = null;
      
      try {
        const comics = await getRelatedComics(comicId, 5);
        relatedComics.value = comics;
      } catch (err) {
        console.error('获取相关推荐漫画出错:', err);
        relatedComicsError.value = '获取相关推荐失败';
      } finally {
        relatedComicsLoading.value = false;
      }
    };
    
    const navigateToReader = (chapterId) => {
      router.push({ 
        name: 'Reader',
        params: { id: chapterId }
      });
    };

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    onMounted(() => {
      fetchComicDetail();
    });

    return {
      comicDetail,
      loading,
      error,
      fetchComicDetail,
      relatedComics,
      relatedComicsLoading,
      relatedComicsError,
      BASE_URL,
      navigateToReader,
      formatTimestamp
    };
  }
};
</script>

<style scoped>
.comic-detail-page {
  display: flex;
  min-height: 100vh;
}

.comic-detail-container {
  margin-left: 200px; /* 与侧边栏宽度相同 */
  width: calc(100% - 200px);
  padding: 20px;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.comic-detail-content {
  flex: 1;
}

.comic-detail {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #ff5a5f;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-small {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100px;
}

.loading-spinner-small {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #ff5a5f;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.loading-text-small {
  font-size: 14px;
  color: #666;
}

.error-message-small {
  text-align: center;
  color: #ff5a5f;
  padding: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
}

.error-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #ff5a5f;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  margin-bottom: 20px;
}

.error-message {
  font-size: 18px;
  color: #333;
  margin-bottom: 20px;
}

.retry-button {
  padding: 10px 20px;
  background-color: #ff5a5f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.retry-button:hover {
  background-color: #ff3c41;
}

.comic-header {
  display: flex;
  margin-bottom: 30px;
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}

.comic-cover {
  flex: 0 0 300px;
  margin-right: 30px;
}

.comic-cover img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.comic-info {
  flex: 1;
}

.comic-title {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.comic-meta {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 15px;
}

.comic-meta span {
  margin-right: 20px;
  margin-bottom: 10px;
  color: #666;
}

.comic-tags {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 15px;
}

.tag {
  padding: 5px 10px;
  background-color: #f5f5f5;
  color: #666;
  border-radius: 15px;
  margin-right: 10px;
  margin-bottom: 10px;
  font-size: 14px;
}

.comic-price {
  margin-bottom: 15px;
}

.price {
  font-size: 20px;
  color: #ff5a5f;
  font-weight: bold;
  margin-right: 10px;
}

.free-chapters {
  color: #666;
}

.comic-description {
  color: #333;
  line-height: 1.6;
  margin-bottom: 20px;
}

.comic-chapters, .related-comics {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.chapters-title, .related-title {
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
  border-left: 5px solid #ff5a5f;
  padding-left: 15px;
}

.chapters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.chapter-item {
  padding: 15px;
  border-radius: 8px;
  background-color: #f9f9f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  height: auto; /* 改为自适应高度 */
  min-height: 80px; /* 最小高度 */
  box-sizing: border-box;
}

.chapter-item:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.chapter-item.free {
  border-left: 3px solid #4CAF50;
}

.chapter-item.paid {
  border-left: 3px solid #ff5a5f;
}

.chapter-info {
  display: flex;
  flex-direction: column;
  width: calc(100% - 70px); /* 为按钮预留空间 */
  overflow: hidden;
}

.chapter-title {
  font-weight: bold;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.chapter-number {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.chapter-status {
  display: flex;
  align-items: center;
}

.free-tag, .price-tag {
  display: inline-block;
  font-size: 14px;
  padding: 2px 0;
}

.free-tag {
  color: #4CAF50;
}

.price-tag {
  color: #ff5a5f;
}

.read-button {
  padding: 8px 15px;
  background-color: #ff5a5f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  min-width: 60px;
  text-align: center;
  height: 36px;
  line-height: 20px;
}

.read-button:hover {
  background-color: #ff3c41;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
}

.related-comic-item {
  display: block;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
}

.related-comic-item:hover {
  transform: translateY(-5px);
}

.related-comic-cover {
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 3/4;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.related-comic-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.related-comic-info {
  padding: 5px;
}

.related-comic-title {
  font-weight: bold;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.related-comic-author {
  font-size: 12px;
  color: #666;
}

@media (max-width: 1200px) {
  .chapters-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .related-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

@media (max-width: 992px) {
  .chapters-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .related-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .comic-header {
    flex-direction: column;
  }
  
  .comic-cover {
    margin-right: 0;
    margin-bottom: 20px;
    flex: 0 0 auto;
    max-width: 250px;
  }
  
  .chapters-grid {
    grid-template-columns: 1fr;
  }
  
  .related-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style> 